import { useRef, useState } from "react";
import { Tabs } from "@mantine/core";
import { FaChartBar } from "react-icons/fa";
import { BiLoaderAlt } from "react-icons/bi";
import { HiDownload, HiChevronDown } from "react-icons/hi";
import { connect } from "react-redux";
import MetaHead from "../../../Common/Metahead";
import AbandonedCart from "./AbandonedCart";
import Marketing from "./Marketing";
import Checkout from "./Checkout";
import Customize from "./Customize";
import DateFilter, { getDateParams } from "../../../Common/DateFilter";
import Sorting from "../../../Common/Sorting";
import { trackingLeadsExport } from "../../../../stores/actions/tracking.actions.types";

const sourceValues = [
  { label: "All Sources", value: "" },
  { label: "Google Ads", value: "google_ads" },
  { label: "Facebook", value: "facebook" },
  { label: "Meta", value: "meta" },
  { label: "Microsoft Ads", value: "microsoft_ads" },
  { label: "Organic", value: "organic" },
  { label: "Direct", value: "direct" },
  { label: "Email", value: "email" },
  { label: "Referral", value: "referral" },
  { label: "TikTok", value: "tiktok" },
  { label: "Twitter", value: "twitter" },
];

const customizeSourceValues = [
  ...sourceValues,
  { label: "Customize", value: "customize" },
  { label: "Customize Builder", value: "customize_builder" },
  { label: "Custom Wheel 2026", value: "custom_wheel_2026" },
];

const FILE_NAMES = {
  "abandoned-cart": "abandoned_cart",
  marketing: "marketing_leads",
  checkout: "checkout_leads",
  customize: "customize_leads",
};

const EVENT_TYPE_MAP = {
  "abandoned-cart": "abandoned-cart",
  marketing: "product_view",
  checkout: "checkout",
  customize: "customize",
};

const Leads = (props) => {
  const [dateParams, setDateParams] = useState(getDateParams("today"));
  const [source, setSource] = useState("");
  const [activeTab, setActiveTab] = useState("abandoned-cart");
  const [exporting, setExporting] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const exportRef = useRef(null);

  const handleClickOutside = (e) => {
    if (exportRef.current && !exportRef.current.contains(e.target)) {
      setExportOpen(false);
    }
  };

  useState(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleExport = (format) => {
    setExporting(true);
    const params = { format, eventType: EVENT_TYPE_MAP[activeTab] };
    if (source) params.source = source;
    props.trackingLeadsExport({
      data: params,
      callback: ({ success, blob, format: fmt }) => {
        setExporting(false);
        if (success && blob) {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
          link.download = `${FILE_NAMES[activeTab] || "leads"}_${timestamp}.${fmt === "excel" ? "xlsx" : "csv"}`;
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        }
      },
    });
  };

  return (
    <div>
      <MetaHead title='Tracking Leads' />
      <Tabs
        defaultValue='abandoned-cart'
        onChange={(val) => {
          setActiveTab(val);
          setDateParams(getDateParams("today"));
          setSource("");
        }}
      >
        <div className='flex items-center justify-between flex-wrap gap-2 mb-1'>
          <Tabs.List>
            <Tabs.Tab
              className='hover:bg-gray-300 dark:hover:bg-gray-800'
              value='abandoned-cart'
              leftSection={<FaChartBar size={18} />}
            >
              Abandoned Cart
            </Tabs.Tab>
            <Tabs.Tab
              className='hover:bg-gray-300 dark:hover:bg-gray-800'
              value='marketing'
              leftSection={<FaChartBar size={18} />}
            >
              Marketing
            </Tabs.Tab>
            <Tabs.Tab
              className='hover:bg-gray-300 dark:hover:bg-gray-800'
              value='checkout'
              leftSection={<FaChartBar size={18} />}
            >
              Checkout
            </Tabs.Tab>
            <Tabs.Tab
              className='hover:bg-gray-300 dark:hover:bg-gray-800'
              value='customize'
              leftSection={<FaChartBar size={18} />}
            >
              Customize
            </Tabs.Tab>
          </Tabs.List>

          <div className='flex items-center gap-2'>
            <Sorting
              key={`source-${activeTab}`}
              name='Source'
              values={activeTab === "customize" ? customizeSourceValues : sourceValues}
              onChange={setSource}
            />
            <DateFilter key={activeTab} onChange={setDateParams} />
            <div className='relative' ref={exportRef}>
              <button
                onClick={() => setExportOpen((prev) => !prev)}
                disabled={exporting}
                className='inline-flex items-center gap-1.5 px-3 py-[7px] rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-boxdark text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
              >
                {exporting ? (
                  <BiLoaderAlt className='animate-spin' size={15} />
                ) : (
                  <HiDownload size={15} />
                )}
                Export
                <HiChevronDown
                  size={14}
                  className={`transition-transform ${exportOpen ? "rotate-180" : ""}`}
                />
              </button>
              {exportOpen && (
                <div className='absolute right-0 mt-1 w-36 rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-boxdark shadow-lg z-10'>
                  <button
                    onClick={() => {
                      setExportOpen(false);
                      handleExport("csv");
                    }}
                    className='w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors'
                  >
                    Export as CSV
                  </button>
                  <button
                    onClick={() => {
                      setExportOpen(false);
                      handleExport("excel");
                    }}
                    className='w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors'
                  >
                    Export as Excel
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <Tabs.Panel value='abandoned-cart'>
          <div className='my-5'>
            {activeTab === "abandoned-cart" && (
              <AbandonedCart dateParams={dateParams} source={source} />
            )}
          </div>
        </Tabs.Panel>
        <Tabs.Panel value='marketing'>
          <div className='my-5'>
            {activeTab === "marketing" && <Marketing dateParams={dateParams} source={source} />}
          </div>
        </Tabs.Panel>
        <Tabs.Panel value='checkout'>
          <div className='my-5'>
            {activeTab === "checkout" && <Checkout dateParams={dateParams} source={source} />}
          </div>
        </Tabs.Panel>
        <Tabs.Panel value='customize'>
          <div className='my-5'>
            {activeTab === "customize" && <Customize dateParams={dateParams} source={source} />}
          </div>
        </Tabs.Panel>
      </Tabs>
    </div>
  );
};

const mapStateToProps = () => ({});

export default connect(mapStateToProps, { trackingLeadsExport })(Leads);
